import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client.js";

export default function AddressAutocompleteField({ label, placeholder, value, onChange }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(false);
  const requestRef = useRef(0);
  const emittedValueRef = useRef(null);
  const MIN_INPUT = 5;

  useEffect(() => {
    const nextValue = value || "";

    setQuery(nextValue);

    if (nextValue === emittedValueRef.current) {
      emittedValueRef.current = null;

      return;
    }

    setShouldSearch(false);
  }, [value]);

  useEffect(() => {
    const trimmed = query.trim();

    if (!isFocused || !shouldSearch || trimmed.length < MIN_INPUT) {
      requestRef.current += 1;
      setSuggestions([]);
      setIsLoading(false);

      return undefined;
    }

    const currentRequest = requestRef.current + 1;
    requestRef.current = currentRequest;
    setIsLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const results = await apiFetch(`/places/autocomplete?input=${encodeURIComponent(trimmed)}`);

        if (requestRef.current === currentRequest) {
          setSuggestions(results);
          setIsOpen(true);
        }
      } catch {
        if (requestRef.current === currentRequest) {
          setSuggestions([]);
        }
      } finally {
        if (requestRef.current === currentRequest) {
          setIsLoading(false);
        }
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [isFocused, query, shouldSearch]);

  function selectSuggestion(suggestion) {
    emittedValueRef.current = suggestion.fullText;
    setQuery(suggestion.fullText);
    setSuggestions([]);
    setIsOpen(false);
    setShouldSearch(false);
    onChange(suggestion.fullText);
  }

  return (
    <div className="address-autocomplete">
      <label className="address-autocomplete-label">{label}</label>
      <input
        className="address-autocomplete-input"
        type="text"
        placeholder={placeholder}
        value={query}
        onFocus={() => {
          setIsFocused(true);

          if (suggestions.length > 0) {
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          setIsFocused(false);
          setShouldSearch(false);
          window.setTimeout(() => setIsOpen(false), 150);
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          emittedValueRef.current = nextValue;
          setQuery(nextValue);
          setShouldSearch(true);
          onChange(nextValue);
        }}
        autoComplete="off"
        spellCheck="false"
      />

      {isLoading && isFocused && shouldSearch && query.trim().length >= MIN_INPUT ? (
        <div className="address-autocomplete-hint">Searching addresses...</div>
      ) : null}

      {isOpen && suggestions.length > 0 ? (
        <div className="address-autocomplete-menu">
          {suggestions.map((suggestion) => (
            <button
              key={`${suggestion.placeId}-${suggestion.fullText}`}
              type="button"
              className="address-autocomplete-option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
            >
              <strong>{suggestion.mainText}</strong>
              {suggestion.secondaryText ? <span>{suggestion.secondaryText}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
